import React from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { FileUp, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Application } from "@/contexts/ApplicationContext";
import { usePersonalDetails } from "@/hooks/usePersonalDetails";
import DocumentPreview from "@/components/application/DocumentPreview";

interface BankDetailsSectionProps {
  application: Application;
  setApplication: React.Dispatch<React.SetStateAction<Application>>;
  bankProofFile: File | null;
  setBankProofFile: (file: File | null) => void;
  bankProofPreview: string | null;
  setBankProofPreview: (preview: string | null) => void;
}

const BankDetailsSection: React.FC<BankDetailsSectionProps> = ({
  application,
  setApplication,
  bankProofFile,
  setBankProofFile,
  bankProofPreview,
  setBankProofPreview,
}) => {
  // Get upload progress from our hook
  const { isBankProofUploading } = usePersonalDetails();
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setBankProofFile(file);
      const fileUrl = URL.createObjectURL(file);
      setBankProofPreview(fileUrl);
    }
  };

  const accountNameTooLong =
    application.personalDetails.accountName.length > 18;

  const handleAccountNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Prevent typing beyond 18 characters
    if (value.length <= 18) {
      setApplication((prev) => ({
        ...prev,
        personalDetails: {
          ...prev.personalDetails,
          accountName: value,
        },
      }));
    }
  };

  const triggerFileInput = () => {
    const input = document.getElementById("bankProof") as HTMLInputElement;
    if (input) {
      input.click();
    }
  };

  const validateAccountNumber = (accountNumber: string): boolean => {
    // UK account numbers are typically 8 digits
    const cleanNumber = accountNumber.replace(/\D/g, "");
    return cleanNumber.length === 8;
  };

  const validateSortCode = (sortCode: string): boolean => {
    // UK sort codes are 6 digits, often formatted as XX-XX-XX
    const cleanSortCode = sortCode.replace(/\D/g, "");
    return cleanSortCode.length === 6;
  };

  const formatSortCode = (value: string): string => {
    const cleanValue = value.replace(/\D/g, "");
    if (cleanValue.length <= 2) return cleanValue;
    if (cleanValue.length <= 4)
      return `${cleanValue.slice(0, 2)}-${cleanValue.slice(2)}`;
    return `${cleanValue.slice(0, 2)}-${cleanValue.slice(
      2,
      4
    )}-${cleanValue.slice(4, 6)}`;
  };

  const handleAccountNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value.replace(/\D/g, "");
    if (value.length <= 8) {
      setApplication((prev) => ({
        ...prev,
        personalDetails: {
          ...prev.personalDetails,
          accountNumber: value,
        },
      }));
    }
  };

  const handleSortCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const formattedValue = formatSortCode(value);
    if (formattedValue.replace(/\D/g, "").length <= 6) {
      setApplication((prev) => ({
        ...prev,
        personalDetails: {
          ...prev.personalDetails,
          sortCode: formattedValue,
        },
      }));
    }
  };

  const removeFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    setBankProofFile(null);
    setBankProofPreview(null);
    // Clear the file input
    const fileInput = document.getElementById("bankProof") as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold mb-4 text-left">Bank Account Details</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="bankName" className="text-left">
            Bank Name
          </Label>
          <Input
            id="bankName"
            value={application.personalDetails.bankName}
            onChange={(e) =>
              setApplication((prev) => ({
                ...prev,
                personalDetails: {
                  ...prev.personalDetails,
                  bankName: e.target.value,
                },
              }))
            }
            placeholder="Enter your bank name"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="accountName" className="text-left">
            Name on Account
          </Label>
          <Input
            id="accountName"
            value={application.personalDetails.accountName}
            // maxLength={18}
            onChange={handleAccountNameChange}
            placeholder="Enter the name on your account"
            className={
              accountNameTooLong ? "border-red-500 focus-visible:ring-red-500" : ""
            }
          />
          {accountNameTooLong && (
            <p className="text-sm text-red-500 text-left">
              Name on account cannot exceed 18 characters
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="accountNumber" className="text-left">
            Account Number
          </Label>
          <Input
            id="accountNumber"
            value={application.personalDetails.accountNumber}
            onChange={handleAccountNumberChange}
            placeholder="12345678"
            maxLength={8}
            className={
              !validateAccountNumber(
                application.personalDetails.accountNumber
              ) && application.personalDetails.accountNumber
                ? "border-red-500"
                : ""
            }
          />
          {!validateAccountNumber(application.personalDetails.accountNumber) &&
            application.personalDetails.accountNumber && (
              <p className="text-sm text-red-500 text-left">
                Account number must be 8 digits
              </p>
            )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="sortCode" className="text-left">
            Sort Code
          </Label>
          <Input
            id="sortCode"
            value={application.personalDetails.sortCode}
            onChange={handleSortCodeChange}
            placeholder="12-34-56"
            maxLength={8}
            className={
              !validateSortCode(application.personalDetails.sortCode) &&
                application.personalDetails.sortCode
                ? "border-red-500"
                : ""
            }
          />
          {!validateSortCode(application.personalDetails.sortCode) &&
            application.personalDetails.sortCode && (
              <p className="text-sm text-red-500 text-left">
                Sort code must be 6 digits (format: XX-XX-XX)
              </p>
            )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="bankProof" className="text-left">
          Proof of Bank Details *
        </Label>
        <div
          className="border-2 border-dashed border-gray-300 rounded-md p-4 flex flex-col items-center justify-center cursor-pointer hover:border-gray-400 transition-colors min-h-32"
          onClick={triggerFileInput}
        >
          {bankProofPreview ? (
            <div className="flex flex-col items-center space-y-2">
              <DocumentPreview
                src={bankProofPreview}
                alt="Bank proof preview"
                file={bankProofFile}
                imageClassName="max-h-32 object-contain"
              />
              {isBankProofUploading && (
                <div className="w-full space-y-2">
                  <p className="text-xs text-center text-gray-500">
                    Uploading...
                  </p>
                </div>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={removeFile}
                disabled={isBankProofUploading}
              >
                <X className="h-4 w-4 mr-2" />
                Remove
              </Button>
            </div>
          ) : (
            <>
              <FileUp className="h-8 w-8 text-gray-400 mb-2" />
              <p className="text-sm text-gray-500">
                Click to upload bank statement or screenshot
              </p>
              <p className="text-xs text-gray-400">
                PNG, JPG or PDF (max. 10MB)
              </p>
            </>
          )}
        </div>
        <Input
          id="bankProof"
          type="file"
          className="hidden"
          onChange={handleFileChange}
          accept="image/png,image/jpeg,application/pdf"
        />
        {!bankProofFile && (
          <p className="text-sm text-red-500 text-left">
            Proof of bank details is required to save
          </p>
        )}
      </div>
    </div>
  );
};

export default BankDetailsSection;
